import InputControl from '../../../components/InputControl/InputControl';
import InteractiveButton from '../../../components/InteractiveButton/InteractiveButton';
import { FormikConfig, useFormik } from 'formik';
import { IRecipeStepEditable, StepMode } from '../../../types/recipes-types';
import { RadioInput } from './RadioInput';
import React, { useMemo } from 'react';
import Switcher, { ResponseSwitch } from '../../../components/Switcher/Switcher';
import CycleMeasurementSymbol from '../../../enums/cycle-measurement-symbol';
import CycleMeasurement from '../../../enums/cycle-measurement';

export interface RecipeStepFormProps<T = IRecipeStepEditable> {
  initialValues: T;
  validationSchema: FormikConfig<T>['validationSchema'];
  onSubmit: (values: T) => any;
  formName?: string;
  submitButtonName: string;
  prefferedUnits?: CycleMeasurement;
  disableUnitsSwitcher?: boolean;
}

export const RecipeStepForm: React.FC<RecipeStepFormProps> = ({
  initialValues,
  validationSchema,
  onSubmit,
  formName,
  submitButtonName,
  prefferedUnits,
  disableUnitsSwitcher = false,
}) => {
  const initialState = useMemo(() => {
    if (!prefferedUnits) {
      return initialValues;
    }
    const isCelsius = prefferedUnits === CycleMeasurement.Celsius;

    return { ...initialValues, isCelsius };
  }, [initialValues, prefferedUnits]);

  const formik = useFormik({
    initialValues: initialState,
    validationSchema: validationSchema,
    onSubmit: onSubmit,
  });
  const temperatureSymbol = formik.values.isCelsius
    ? CycleMeasurementSymbol.Celsius
    : CycleMeasurementSymbol.Fahrenheit;

  const onUnitsChange = (data: ResponseSwitch) => {
    formik.setFieldValue('isCelsius', data.value);
  };

  return (
    <>
      {!!formName && (
        <div className="border-b border-stroke px-7 py-4 dark:border-strokedark">
          <h3 className="font-medium text-black dark:text-white">{formName}</h3>
        </div>
      )}
      <form className="flex flex-col gap-5.5 p-7" onSubmit={formik.handleSubmit}>
        <div className="w-full">
          <div className="c-input-control">
            <label className="c-input-control__label flex justify-between pb-2.5 font-medium text-black dark:text-white">
              Mode
            </label>

            <div className="c-input-control__input-wrapper relative flex flex-row gap-x-5">
              <RadioInput
                onChange={formik.handleChange}
                checked={formik.values.mode === StepMode.Rate}
                name="mode"
                value={StepMode.Rate}
                label={StepMode.Rate}
              />
              <RadioInput
                onChange={formik.handleChange}
                checked={formik.values.mode === StepMode.Soak}
                value={StepMode.Soak}
                name="mode"
                label={StepMode.Soak}
              />
            </div>
            <div className="flex min-h-6 items-center justify-start"></div>
          </div>

          {formik.values.mode === StepMode.Soak && (
            <>
              <InputControl
                label={'Days'}
                type={'number'}
                name={'durationDays'}
                placeholder={'Enter duration'}
                value={formik.values.durationDays}
                customOnChange={formik.handleChange}
                onBlur={formik.handleBlur}
                showError={formik.touched.durationDays && formik.errors.durationDays}
                errorText={formik.errors.durationDays}
              />
              <InputControl
                label={'Hours'}
                type={'number'}
                name={'durationHours'}
                placeholder={'Enter duration'}
                value={formik.values.durationHours}
                customOnChange={formik.handleChange}
                onBlur={formik.handleBlur}
                showError={formik.touched.durationHours && formik.errors.durationHours}
                errorText={formik.errors.durationHours}
              />
              <InputControl
                label={'Minutes'}
                type={'number'}
                name={'durationMinutes'}
                placeholder={'Enter duration'}
                value={formik.values.durationMinutes}
                customOnChange={formik.handleChange}
                onBlur={formik.handleBlur}
                showError={formik.touched.durationMinutes && formik.errors.durationMinutes}
                errorText={formik.errors.durationMinutes}
              />
            </>
          )}

          {formik.values.mode === StepMode.Rate && (
            <>
              <div className="c-input-control__input-wrapper">
                <p className="flex justify-between pb-2.5 font-medium text-black dark:text-white">Units</p>
                <Switcher
                  options={{
                    left: CycleMeasurementSymbol.Celsius,
                    right: CycleMeasurementSymbol.Fahrenheit,
                  }}
                  isEnable={formik.values.isCelsius}
                  onChange={onUnitsChange}
                  switcherId={`${formik.values.order}-step-units-id`}
                  disabled={disableUnitsSwitcher}
                />
                <div className="flex min-h-6 items-center justify-start"></div>
              </div>
              <InputControl
                label={`${temperatureSymbol}/h`}
                type={'number'}
                name={'heatingRate'}
                placeholder={'Enter heating rate'}
                value={formik.values.heatingRate}
                customOnChange={formik.handleChange}
                onBlur={formik.handleBlur}
                showError={formik.touched.heatingRate && formik.errors.heatingRate}
                errorText={formik.errors.heatingRate}
              />
              <InputControl
                label={`To (${temperatureSymbol})`}
                type={'number'}
                name={'heatingLimit'}
                placeholder={'Enter heating limit'}
                value={formik.values.heatingLimit}
                customOnChange={formik.handleChange}
                onBlur={formik.handleBlur}
                showError={formik.touched.heatingLimit && formik.errors.heatingLimit}
                errorText={formik.errors.heatingLimit}
              />
            </>
          )}

          <InteractiveButton text={submitButtonName} submit isLoading={formik.isSubmitting} />
        </div>
      </form>
    </>
  );
};
